import React, { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Plus, Pencil, Trash2, MapPin, Warehouse } from "lucide-react";
import { toast } from "sonner";

const EMPTY = { name: "", address: "", notes: "" };

export default function AdminStartLocations() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null); // null = create
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);

  const load = () =>
    api.get("/start_locations").then((r) => setItems(r.data)).catch(() => setItems([]));

  useEffect(() => {
    load();
  }, []);

  const openCreate = () => {
    setEditing(null);
    setForm(EMPTY);
    setOpen(true);
  };
  const openEdit = (loc) => {
    setEditing(loc);
    setForm({ name: loc.name || "", address: loc.address || "", notes: loc.notes || "" });
    setOpen(true);
  };

  const submit = async () => {
    if (!form.name.trim()) return toast.error("Name is required");
    if (!form.address.trim()) return toast.error("Address is required");
    setBusy(true);
    try {
      if (editing) {
        await api.put(`/start_locations/${editing.id}`, form);
        toast.success("Start location updated");
      } else {
        await api.post("/start_locations", form);
        toast.success("Start location created");
      }
      setOpen(false);
      setForm(EMPTY);
      setEditing(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const remove = async (loc) => {
    if (!confirm(`Delete start location "${loc.name}"?`)) return;
    try {
      await api.delete(`/start_locations/${loc.id}`);
      toast.success("Deleted");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div className="p-6 lg:p-10 max-w-[1400px] mx-auto" data-testid="admin-start-locations-page">
      <header className="mb-6 flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-3xl lg:text-4xl font-display font-bold tracking-tight">Start Locations</h1>
          <p className="text-muted-foreground mt-1.5">
            Saved warehouses, depots, or hubs that dispatchers can pick as a route start — in addition to pharmacies and the company headquarters.
          </p>
        </div>
        <Button className="rounded-full" onClick={openCreate} data-testid="new-start-location-btn">
          <Plus className="h-4 w-4 mr-1.5" /> New start location
        </Button>
      </header>

      {items.length === 0 ? (
        <Card className="p-10 rounded-xl border-border bg-card/80 text-center" data-testid="start-locations-empty">
          <Warehouse className="h-8 w-8 mx-auto text-muted-foreground mb-3" />
          <div className="font-display font-semibold">No start locations yet</div>
          <p className="text-sm text-muted-foreground mt-1">
            Add depots, warehouses, or any custom origin you want available in the Route Planner start dropdown.
          </p>
        </Card>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="start-locations-grid">
          {items.map((loc) => (
            <Card key={loc.id} className="p-5 rounded-xl border-border bg-card/80" data-testid={`start-location-${loc.id}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="font-display font-semibold text-base flex items-center gap-1.5">
                    <Warehouse className="h-4 w-4 text-primary" /> {loc.name}
                  </div>
                  <div className="mt-1.5 text-sm text-muted-foreground flex items-start gap-1">
                    <MapPin className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                    <span>{loc.address}</span>
                  </div>
                  {loc.notes && (
                    <div className="mt-2 text-xs text-muted-foreground bg-secondary/40 rounded-lg p-2">
                      {loc.notes}
                    </div>
                  )}
                  {loc.lat != null && loc.lng != null && (
                    <div className="mt-2 text-[10px] text-muted-foreground font-mono-num">
                      {loc.lat.toFixed(4)}, {loc.lng.toFixed(4)}
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <Button variant="ghost" size="icon" onClick={() => openEdit(loc)} className="h-8 w-8" data-testid={`edit-start-location-${loc.id}`} title="Edit">
                    <Pencil className="h-3.5 w-3.5" />
                  </Button>
                  <Button variant="ghost" size="icon" onClick={() => remove(loc)} className="h-8 w-8 text-muted-foreground hover:text-destructive" data-testid={`delete-start-location-${loc.id}`} title="Delete">
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={(v) => { if (!v) { setOpen(false); setEditing(null); setForm(EMPTY); } }}>
        <DialogContent data-testid="start-location-dialog">
          <DialogHeader>
            <DialogTitle>{editing ? "Edit start location" : "New start location"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Name</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="North Miami Depot" data-testid="start-location-name" />
            </div>
            <div>
              <Label>Address</Label>
              <Input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} placeholder="100 NW 1st St, Miami, FL 33128" data-testid="start-location-address" />
              <p className="mt-1 text-[11px] text-muted-foreground">Address is geocoded automatically. If it can't be located, you'll get an error.</p>
            </div>
            <div>
              <Label>Notes (optional)</Label>
              <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Refrigerated storage; loading bay 3…" data-testid="start-location-notes" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => { setOpen(false); setEditing(null); setForm(EMPTY); }}>Cancel</Button>
            <Button onClick={submit} disabled={busy} className="rounded-full" data-testid="start-location-submit">
              {busy ? "Saving…" : editing ? "Save changes" : "Create"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
